/**
 * AAAprinters - Supabase Authentication Helper Functions
 * Complete authentication flow implementation
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  SignUpData,
  SignInData,
  ResetPasswordData,
  UpdatePasswordData,
  Profile,
  ProfileUpdate
} from '@/types/supabase-auth';
import type { User, AuthError, Session } from '@supabase/supabase-js';

// =====================================================
// AUTHENTICATION FUNCTIONS
// =====================================================

/**
 * Sign up a new user with email and password
 */
export async function signUp(data: SignUpData) {
  const { email, password, full_name, full_name_ar, phone, preferred_language } = data;

  const { data: authData, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: full_name || email.split('@')[0],
        full_name_ar: full_name_ar || null,
        phone: phone || null,
        preferred_language: preferred_language || 'en'
      },
      emailRedirectTo: `${window.location.origin}/auth/callback`
    }
  });

  if (error) {
    throw new Error(error.message);
  }

  return {
    user: authData.user,
    session: authData.session
  };
}

/**
 * Sign in with email and password
 */
export async function signIn(data: SignInData) {
  const { email, password } = data;

  const { data: authData, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) {
    throw new Error(error.message);
  }

  return {
    user: authData.user,
    session: authData.session
  };
}

/**
 * Sign out current user
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<User | null> {
  const {
    data: { user },
    error
  } = await supabase.auth.getUser();

  if (error) {
    console.error('Error getting current user:', error);
    return null;
  }

  return user;
}

/**
 * Get current session
 */
export async function getCurrentSession(): Promise<Session | null> {
  const {
    data: { session },
    error
  } = await supabase.auth.getSession();

  if (error) {
    console.error('Error getting session:', error);
    return null;
  }

  return session;
}

/**
 * Send password reset email
 */
export async function resetPassword(data: ResetPasswordData) {
  const { email } = data;

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`
  });

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Update user password (requires authenticated user)
 */
export async function updatePassword(data: UpdatePasswordData) {
  const { password } = data;

  const { error } = await supabase.auth.updateUser({
    password
  });

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Update user email (requires authenticated user)
 */
export async function updateEmail(newEmail: string) {
  const { error } = await supabase.auth.updateUser({
    email: newEmail
  });

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Refresh session
 */
export async function refreshSession() {
  const { data, error } = await supabase.auth.refreshSession();

  if (error) {
    throw new Error(error.message);
  }

  return data.session;
}

// =====================================================
// PROFILE FUNCTIONS
// =====================================================

/**
 * Get user profile by ID
 */
export async function getProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  return data;
}

/**
 * Get current user's profile
 */
export async function getCurrentUserProfile(): Promise<Profile | null> {
  const user = await getCurrentUser();

  if (!user) {
    return null;
  }

  return getProfile(user.id);
}

/**
 * Update user profile
 */
export async function updateProfile(userId: string, updates: ProfileUpdate): Promise<Profile> {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to update profile: ${error.message}`);
  }

  return data;
}

/**
 * Update current user's profile
 */
export async function updateCurrentUserProfile(updates: ProfileUpdate): Promise<Profile> {
  const user = await getCurrentUser();

  if (!user) {
    throw new Error('No authenticated user');
  }

  return updateProfile(user.id, updates);
}

/**
 * Upload avatar image
 */
export async function uploadAvatar(file: File, userId: string): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const fileName = `${userId}-${Date.now()}.${fileExt}`;
  const filePath = `avatars/${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: true
    });

  if (uploadError) {
    throw new Error(`Failed to upload avatar: ${uploadError.message}`);
  }

  const {
    data: { publicUrl }
  } = supabase.storage.from('avatars').getPublicUrl(filePath);

  // Update profile with new avatar URL
  await updateProfile(userId, { avatar_url: publicUrl });

  return publicUrl;
}

/**
 * Delete avatar image
 */
export async function deleteAvatar(userId: string, avatarUrl: string): Promise<void> {
  // Extract file path from URL
  const urlParts = avatarUrl.split('/');
  const filePath = `avatars/${urlParts[urlParts.length - 1]}`;

  const { error } = await supabase.storage.from('avatars').remove([filePath]);

  if (error) {
    throw new Error(`Failed to delete avatar: ${error.message}`);
  }

  // Update profile to remove avatar URL
  await updateProfile(userId, { avatar_url: null });
}

// =====================================================
// AUTH STATE LISTENERS
// =====================================================

/**
 * Listen to authentication state changes
 */
export function onAuthStateChange(callback: (user: User | null, session: Session | null) => void) {
  const {
    data: { subscription }
  } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session?.user || null, session);
  });

  return subscription;
}

/**
 * Unsubscribe from auth state changes
 */
export function unsubscribeAuthStateChange(subscription: ReturnType<typeof onAuthStateChange>) {
  subscription.unsubscribe();
}

// =====================================================
// SOCIAL AUTHENTICATION (Optional)
// =====================================================

/**
 * Sign in with Google
 */
export async function signInWithGoogle() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });

  if (error) {
    throw new Error(error.message);
  }
}

/**
 * Sign in with Apple
 */
export async function signInWithApple() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'apple',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });

  if (error) {
    throw new Error(error.message);
  }
}

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getCurrentSession();
  return session !== null;
}

/**
 * Check if user email is verified
 */
export async function isEmailVerified(): Promise<boolean> {
  const user = await getCurrentUser();
  return user?.email_confirmed_at !== undefined;
}

/**
 * Resend verification email
 */
export async function resendVerificationEmail(email: string) {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email
  });

  if (error) {
    throw new Error(error.message);
  }
}
